import { DropdownMenu } from "@/components/molecules/DropdownMenu/DropdownMenu";
import { parseHexWithAlpha } from "@/layers/utility/color";
import {
  GenericColorPicker,
  type GenericColorPickerSavedColorsState,
} from "@/overlay/components/GenericColorPicker/GenericColorPicker";
import { SavedColorsCloseConfirmationModal } from "@/overlay/components/GenericColorPicker/SavedColorsCloseConfirmationModal";
import {
  type SavedColorsCloseConfirmationConfig,
  requestSavedColorsClose,
} from "@/overlay/components/GenericColorPicker/requestSavedColorsClose";
import { useCallback, useEffect, useRef, useState } from "react";
import { type IColor, useColor } from "react-color-palette";
import styles from "./CompactColorPickerInput.module.scss";

export function CompactColorPickerInput(props: {
  value: string;
  change: (color: string) => void;
  closeOnSavedColorSelect?: boolean;
  withinPortal?: boolean;
  placement?: "bottom-start" | "bottom-end";
  registerCloseHandler?: (handler: (() => boolean) | null) => void;
  dropdownZIndex?: number;
}) {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [savedColorsState, setSavedColorsState] = useState<GenericColorPickerSavedColorsState | null>(null);
  const [closeConfirmation, setCloseConfirmation] = useState<SavedColorsCloseConfirmationConfig | null>(null);
  const [color, setColor] = useColor(props.value);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const latestHexRef = useRef(color.hex);
  const isPickerOpenRef = useRef(false);
  const hasCommittedRef = useRef(false);

  useEffect(() => {
    latestHexRef.current = color.hex;
  }, [color.hex]);

  useEffect(() => {
    isPickerOpenRef.current = displayColorPicker;
    if (displayColorPicker) {
      hasCommittedRef.current = false;
    }
  }, [displayColorPicker]);

  const commitColor = useCallback(() => {
    props.change(parseHexWithAlpha(latestHexRef.current));
    hasCommittedRef.current = true;
  }, [props.change]);

  useEffect(() => {
    return () => {
      if (isPickerOpenRef.current && !hasCommittedRef.current) {
        commitColor();
      }
    };
  }, [commitColor]);

  const handleColorChange = useCallback(
    (nextColor: IColor) => {
      latestHexRef.current = nextColor.hex;
      setColor(nextColor);
    },
    [setColor],
  );

  const handleClose = useCallback(() => {
    return requestSavedColorsClose({
      savedColorsState,
      onConfirmedClose: () => {
        setCloseConfirmation(null);
        commitColor();
        setDisplayColorPicker(false);
      },
      confirmUnsavedChanges: (config) => {
        setCloseConfirmation(config);
      },
    });
  }, [commitColor, savedColorsState]);

  useEffect(() => {
    props.registerCloseHandler?.(displayColorPicker ? handleClose : null);

    return () => {
      props.registerCloseHandler?.(null);
    };
  }, [displayColorPicker, handleClose, props.registerCloseHandler]);

  const handleClick = () => {
    if (displayColorPicker) {
      handleClose();
      return;
    }

    setDisplayColorPicker(true);
  };

  return (
    <div ref={wrapperRef}>
      <div className={styles["color-picker"]}>
        <div className={styles["color-picker__swatch"]} onClick={handleClick} data-testid="color-picker-input">
          <div className={styles["color-picker__color"]} style={{ background: color.hex }} />
        </div>
      </div>
      <DropdownMenu
        isOpen={displayColorPicker}
        onClose={handleClose}
        anchorRef={wrapperRef}
        withinPortal={props.withinPortal ?? true}
        portalProps={{ zIndex: props.dropdownZIndex ?? 9999 }}
        placement={props.placement ?? "bottom-start"}
      >
        {closeConfirmation ? (
          <SavedColorsCloseConfirmationModal isOpen={true} {...closeConfirmation} />
        ) : (
          <GenericColorPicker
            color={color}
            onColorChange={handleColorChange}
            closeOnSavedColorSelect={props.closeOnSavedColorSelect}
            onRequestClose={handleClose}
            onSavedColorsStateChange={setSavedColorsState}
          />
        )}
      </DropdownMenu>
    </div>
  );
}
